/**
 * The two accessibility REPORTS for Preview — what a teacher's own content would do to a student
 * who cannot see it the way they do.
 *
 * Pure functions over a DOM root, so they are testable without rendering the editor and reusable
 * from any surface. Nothing here translates: findings carry data, the panel carries words.
 *
 * ── THE SCAN BOUNDARY ─────────────────────────────────────────────────────────────────────────
 * Reports flag ONLY what a teacher authored. A contrast finding against Agavi's own chrome is
 * unfixable by the person reading it, and two or three of those and nobody opens the panel again.
 * So the whole value of this file rests on `isAuthoredContent` being conservative.
 *
 * Missing an authored element is a false NEGATIVE — an unchecked string, still better than a
 * report nobody trusts. Including chrome is a false POSITIVE, which is fatal. When in doubt, skip.
 *
 * Note that the colour-vision simulation applies a CSS `filter`, which does NOT change computed
 * colour values. Contrast is therefore always measured on the TRUE colours, whatever mode the
 * teacher is viewing in. That is correct — the ratio a screen reader user's contrast depends on is
 * the real one — but it looks wrong, so the panel says so.
 */

import { formulaAnnouncement } from './formula-announcement';

/** Authored content carries one of these. `ws-rich-text` is `BLOCK_TEXT_CLASS` (question and
 * feedback bodies), `ws-formula-preview` wraps option and item labels, and `data-authored` is the
 * explicit marker `PreviewHtml` and the few raw renderers emit. */
const AUTHORED_SELECTOR = '[data-authored], .ws-rich-text, .ws-formula-preview';

/**
 * A rendered formula, which is ATOMIC for both reports.
 *
 * KaTeX renders one span per glyph, so walking into one produced a row per CHARACTER — a reading
 * list that went "2", "+", "O", "2", "→" down the panel. Its `textContent` is no better: KaTeX
 * nests the LaTeX source in an `<annotation>` inside the MathML, and mhchem hides a literal "X" in
 * an `<mphantom>` behind every subscript, so the text of H2O is "H X 2 O" and the text of a fraction
 * is the glyphs followed by the backslashes that produced them.
 *
 * So the node is atomic and its announcement is DERIVED, from the authored source on the node's own
 * attribute. `formula-announcement.ts` does that and explains what each path can manage.
 */
const FORMULA_SELECTOR = '[data-math-latex], [data-reaction-formula], .chem-formula, .math-formula';

/**
 * Whether a control is named by the teacher's own words rather than by ours.
 *
 * A control is atomic, so its content never gets a row of its own — which silently took MCQ's
 * option text out of Your Content, because an option is a `role=checkbox` whose label is authored
 * rich text sitting INSIDE it. `isAuthoredContent` could not see that: it walks UP, and the
 * authored marker here is a descendant.
 *
 * An `<img>` with alt counts for the same reason — a media option's name comes from the alt text
 * field, which is a teacher's writing even though no rich-text wrapper is involved.
 *
 * Deliberately false for a control whose name is ours: a Capture button, a nudge, an audio option
 * named "Sound clip 3". Those belong to With Interactives, and putting them under Your Content
 * would tell a teacher they had written something they had not.
 */
function hasAuthoredName(el: Element): boolean {
  /* ITS OWN marker as well as a descendant's. A media option's authored words are in the control's
     own `aria-label` — the picture inside carries `alt=''` deliberately — so it marks ITSELF
     `data-authored`, and a descendant-only search could never see that. */
  if (el.hasAttribute?.('data-authored')) return true;
  if (el.querySelector?.(AUTHORED_SELECTOR)) return true;
  return [...el.querySelectorAll?.('img') ?? []].some(img => (img.getAttribute('alt') ?? '').trim());
}

/** Whether this element sits inside something a teacher wrote. Walks up to `root` and no further —
 * an ancestor outside the previewed task must never make its subtree count as authored. */
export function isAuthoredContent(el: Element, root: Element): boolean {
  let cur: Element | null = el;
  while (cur && cur !== root.parentElement) {
    if (cur.matches?.(AUTHORED_SELECTOR)) return true;
    if (cur === root) return false;
    cur = cur.parentElement;
  }
  return false;
}

// ── Shared traversal ──────────────────────────────────────────────────────────

/**
 * A control Preview renders that the student runner will not: Reroll, the phone-sensor simulator.
 *
 * Preview exists to look like a student's screen, and these two are the acknowledged places where it
 * does not — one is a teacher drawing another variant, the other a teacher standing in for a phone.
 * They are inside the scanned tree because they are inside the bar, and a report that lists them is
 * describing an experience nobody will have.
 *
 * A marked attribute rather than a name check: the names are translated, and a report that goes
 * wrong in pt-BR is worse than one that is wrong everywhere. Whoever adds the next teacher-only
 * control marks it at the point they write it.
 */
const TEACHER_ONLY_SELECTOR = '[data-teacher-only]';

/** Hidden from everyone, so hidden from both reports. `aria-hidden` is deliberate authoring ("this
 * is decorative"), and an unrendered subtree has no contrast to measure. Teacher-only chrome is
 * skipped by the same door: no student meets it, so no report should describe it. */
function isHidden(el: Element): boolean {
  if (el.matches?.(TEACHER_ONLY_SELECTOR)) return true;
  if (el.getAttribute('aria-hidden') === 'true') return true;
  const cs = typeof getComputedStyle === 'function' ? getComputedStyle(el) : null;
  if (!cs) return false;
  return cs.display === 'none' || cs.visibility === 'hidden';
}

function* walk(root: Element): Generator<Element> {
  const stack: Element[] = [root];
  while (stack.length) {
    const el = stack.pop()!;
    if (el !== root && isHidden(el)) continue;
    if (el !== root) yield el;
    const kids = [...el.children];
    for (let i = kids.length - 1; i >= 0; i--) stack.push(kids[i]!);
  }
}

/** In document order, unlike `walk`'s stack. The screen-reader report is ABOUT order, so it cannot
 * use a traversal whose order is an implementation detail. */
function inOrder(root: Element): Element[] {
  const out: Element[] = [];
  const visit = (el: Element) => {
    for (const child of [...el.children]) {
      if (isHidden(child)) continue;
      out.push(child);
      visit(child);
    }
  };
  visit(root);
  return out;
}

// ── Report 1 · what a screen reader would announce ────────────────────────────

export interface OutlineRow {
  /** `heading` | `image` | `control` | `text` — coarse on purpose; this is a reading, not an audit. */
  kind: 'heading' | 'image' | 'control' | 'text' | 'media' | 'formula' | 'reaction' | 'table' | 'feedback';
  /** What would be announced. Empty means nothing would be — which is the finding. */
  name: string;
  /**
   * The row's `aria-describedby` text, kept APART from the name.
   *
   * A reader announces a name and a description as two properties with a pause between them, and
   * they answer different questions — what a thing IS, then what it does. Joined into one string
   * (`alt + '. ' + description`) they read as a single sentence that a teacher cannot take apart,
   * and a missing description looks like a sentence that merely stopped early.
   *
   * On a CONTROL as well as an image: an MCQ media option carries its motion description here,
   * because the option's name lives on the tile and the picture is `alt=''` beneath it. Without
   * this, an animated option announced its alt text and dropped the motion description entirely —
   * the thing the description exists for.
   */
  description?: string;
  /**
   * This row's picture MOVES.
   *
   * Carried so the read-through can label it "Animation" where it would otherwise say "Image",
   * the same service the Formula and Reaction labels already perform: a teacher who has never
   * used a screen reader needs to know why the reading suddenly has two parts to it, and the kind
   * chip is where this panel explains itself. It is the EMULATOR's annotation, not something a
   * reader is sent — a reader infers movement from nothing at all, which is the very gap the
   * motion description exists to fill.
   */
  animated?: true;
  /**
   * What a CONTROL wraps, when it wraps media.
   *
   * An MCQ or Ordering option is a `control` row — its name is on the tile, not on the picture
   * inside it — so the kind alone says nothing about what is in there, and the read-through showed
   * no chip at all beside an image option. The chip is a sighted teacher's index for finding rows
   * worth checking, and a media option is exactly such a row.
   */
  mediaKind?: 'image' | 'audio';
  /*
   * NO `problems` HERE ANY MORE.
   *
   * This row used to carry what it owed — a missing alt, motion description or transcript — for a
   * Problems panel that no longer exists. Every one of those is an error on the block's own card,
   * beside the field that fixes it and gating publication from there, so the scan computing a
   * second opinion from the rendered DOM was the duplication that panel was removed to end. It
   * was dead state: nothing but its own tests read it.
   *
   * What a listener MISSES is still visible here, which is the part that belongs to this panel: a
   * row whose name is empty reads as silence, and the read-through says so in place.
   */
  /** True for content a teacher wrote. The read-through shows only these; problems span both. */
  authored: boolean;
  /** For a control: what it IS, read off the DOM rather than guessed. */
  role?: ControlRole;
  /** For a control: what state it is in. More than one can apply — unchecked AND unavailable. */
  states?: ControlState[];
  /** For a control that reports a value, the value a reader would announce. */
  value?: string;
  /** For a table: what a reader states on entering it. The panel turns these into words. */
  table?: { rows: number; columns: number };
  /** For `feedback`: the verdict the box is carrying, so the panel can speak it. */
  evaluation?: 'correct' | 'incorrect' | 'neutral';
  /**
   * A control that was already spoken inside an authored sentence.
   *
   * A FITB blank sits IN the question and a dropdown can sit in a sentence, so `inlineFlow`
   * substitutes them where they occur — which is where a listener meets them. Emitting a row of
   * their own as well says them twice: "Water boils at Blank 1 of 3 degrees" and then "Blank 1 of
   * 3, text box, empty". The row is still produced, so a tree audit can see every control; the
   * read-through skips the ones already heard.
   */
  inline?: boolean;
  el: Element;
}

/**
 * The accessible name: `aria-label`, then `aria-labelledby`, then `alt`, then the element's content.
 *
 * The content is read through `inlineFlow`, NOT `textContent`. An option whose label is a formula
 * would otherwise be named with the KaTeX glyphs, the LaTeX source and mhchem's phantom X — so the
 * one thing a student hears for that option is the markup, ahead of everything the reaction speech
 * was built to get right.
 */
function accessibleName(el: Element): string {
  const aria = el.getAttribute('aria-label');
  if (aria?.trim()) return aria.trim();
  const labelled = el.getAttribute('aria-labelledby');
  if (labelled) {
    const target = el.ownerDocument?.getElementById(labelled);
    if (target?.textContent?.trim()) return target.textContent.trim();
  }
  if (el.tagName === 'IMG') return (el.getAttribute('alt') ?? '').trim();
  return inlineFlow(el).text;
}

export type ControlRole =
  | 'button' | 'link' | 'checkbox' | 'radio' | 'combobox' | 'textbox' | 'slider'
  | 'switch' | 'tab' | 'menuitem' | 'option' | 'control';

export type ControlState =
  | 'checked' | 'notChecked' | 'mixed' | 'expanded' | 'collapsed' | 'disabled' | 'empty';

/**
 * What a control IS, taken from the DOM rather than inferred.
 *
 * An explicit `role` wins, because a `<div role='checkbox'>` is a checkbox to assistive technology
 * whatever its tag says — and that is not a corner case here: a media MCQ option is exactly that,
 * since HTML forbids the nested Play button a real `<button>` would need.
 */
function controlRole(el: Element): ControlRole {
  const declared = (el.getAttribute('role') ?? '').toLowerCase();
  if (CONTROL_ROLES.has(declared)) return declared as ControlRole;
  switch (el.tagName) {
    case 'A': return 'link';
    case 'BUTTON': return 'button';
    case 'SELECT': return 'combobox';
    case 'TEXTAREA': return 'textbox';
    case 'INPUT': {
      const type = (el.getAttribute('type') ?? 'text').toLowerCase();
      if (type === 'checkbox') return 'checkbox';
      if (type === 'radio') return 'radio';
      if (type === 'range') return 'slider';
      return 'textbox';
    }
    default: return 'control';
  }
}

/**
 * The states a reader would announce, and only the ones actually declared.
 *
 * Absence is deliberate: a button with no `aria-checked` is not "not checked", it simply has no
 * checked state, and saying so would be inventing a reading rather than reporting one.
 */
function controlStates(el: Element): ControlState[] {
  const out: ControlState[] = [];
  const checked = el.getAttribute('aria-checked');
  if (checked === 'true') out.push('checked');
  else if (checked === 'false') out.push('notChecked');
  else if (checked === 'mixed') out.push('mixed');
  else if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) {
    out.push(el.checked ? 'checked' : 'notChecked');
  }
  const expanded = el.getAttribute('aria-expanded');
  if (expanded === 'true') out.push('expanded');
  else if (expanded === 'false') out.push('collapsed');
  if (el.hasAttribute('disabled') || el.getAttribute('aria-disabled') === 'true') out.push('disabled');
  /**
   * An empty text field says so, which is the one state a reader supplies that we were dropping.
   *
   * NVDA says "blank" here and it is load-bearing: a student tabbing an answer box needs to know
   * whether anything is in it, and the panel reported the name and then stopped. Only for a field
   * that can HOLD text — a checkbox has no such state, and a slider always reports a value.
   */
  if (el instanceof HTMLInputElement && TEXTUAL_INPUT_TYPES.has(el.type) && !el.value) out.push('empty');
  else if (el instanceof HTMLTextAreaElement && !el.value) out.push('empty');
  return out;
}

/**
 * Input types that hold text, and so can be empty.
 *
 * Listed rather than excluding checkbox/radio, because the set that CANNOT be empty is open-ended
 * (`range`, `color`, `file` and every future type) while the set that can is small and stable.
 */
const TEXTUAL_INPUT_TYPES = new Set(['text', 'search', 'tel', 'url', 'email', 'password', 'number']);

/**
 * The value a control reports, where it reports one.
 *
 * `aria-valuetext` first and deliberately: a slider sets it to the formatted reading — "10 m/s" —
 * where `aria-valuenow` is the bare number, and the formatted one is what a reader announces.
 */
function controlValue(el: Element): string | undefined {
  const text = el.getAttribute('aria-valuetext');
  if (text?.trim()) return text.trim();
  const now = el.getAttribute('aria-valuenow');
  if (now?.trim()) return now.trim();
  if (el instanceof HTMLInputElement && el.type !== 'checkbox' && el.type !== 'radio' && el.value) {
    return el.value;
  }
  /**
   * A `<select>` reports the option it is SHOWING, not its underlying value.
   *
   * Missed entirely before, because the check above is for inputs — so the sonification player's
   * speed announced as "Playback speed, combo box" and stopped, with the one fact a listener needs
   * from a select left out. Real readers say the option's text, and its text is what is on screen;
   * `value` is an id a student never sees.
   *
   * Second in the fallback chain rather than first, so an explicit `aria-valuetext` still wins.
   */
  if (el instanceof HTMLSelectElement) {
    const chosen = [...el.selectedOptions].map(o => o.text.trim()).filter(Boolean);
    if (chosen.length) return chosen.join(', ');
  }
  /**
   * A combobox that is NOT a `<select>` — the app's own `Dropdown`, a button carrying
   * `role='combobox'` over a portalled listbox.
   *
   * Same fact as the branch above and it needed saying twice, because the two are built
   * differently: a native select holds its choice in `selectedOptions`, and this one simply
   * shows it. The rule a reader follows is the same either way — say the option on screen — so
   * the emulator follows it by reading the trigger's own text rather than by knowing which
   * component drew it. Added when nine native selects became this one, and every one of them
   * silently stopped reporting its value.
   */
  if (el.getAttribute('role') === 'combobox') {
    const shown = (el.textContent ?? '').trim();
    if (shown) return shown;
  }
  if (el instanceof HTMLTextAreaElement && el.value) return el.value;
  return undefined;
}

const CONTROL_TAGS = new Set(['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'A']);

/**
 * ARIA roles that make a non-control element a control.
 *
 * Tag-based detection alone missed these entirely, and it is not a corner case: a media MCQ option
 * is a `<div role='checkbox'>`, because HTML forbids the nested Play button a native `<button>`
 * would need. So the thing a screen reader announces as "checkbox, not checked, <name>" was
 * producing no row at all — it is not a control by tag, and having no own text it never reached the
 * text branch either.
 */
const CONTROL_ROLES = new Set([
  // `combobox` was missing, and its absence was invisible until something declared it: a
  // `<button role='combobox'>` fell through to its TAG NAME and the panel went on reporting
  // "button", so the emulator would have contradicted the markup it exists to describe.
  'button', 'checkbox', 'radio', 'slider', 'switch', 'tab', 'menuitem', 'option', 'link', 'textbox',
  'combobox',
]);

const isControl = (el: Element): boolean =>
  CONTROL_TAGS.has(el.tagName) || CONTROL_ROLES.has((el.getAttribute('role') ?? '').toLowerCase());

/**
 * Controls in this tree with nothing to announce.
 *
 * OURS to fix, never a teacher's: every control here is block-rendered and no authoring surface can
 * produce a nameless one. Exported rather than reported so the fault has somewhere to go that is not
 * a teacher's panel — a rendered-tree test can assert this is empty, which is a check someone who
 * can fix it will actually see.
 *
 * A source-level version of this was tried and thrown away: a sweep for `role=` without an
 * `aria-label` flagged 26 sites, and nearly all were correctly named by their own child text, which
 * a regex cannot see. Demanding a label there would have pressured people into writing junk ones —
 * the same trap `alt=""` sets, noted above.
 */
export function unnamedControls(root: Element): Element[] {
  return [...walk(root)].filter(el => isControl(el) && !accessibleName(el));
}

/**
 * Block-level tags, for finding the unit a listener receives as one flow.
 *
 * TipTap wraps every paragraph in a `<p>`, so the innermost of these is the sentence — which is the
 * unit worth reading, not the individual text nodes inside it.
 */
const BLOCK_TAGS = 'p, div, li, h1, h2, h3, h4, h5, h6, td, th, blockquote, figcaption';

/**
 * One authored block, as the sentence a listener actually hears.
 *
 * Replaces reading an element's OWN text nodes, which silently dropped anything interleaved between
 * them and reported it separately, in the wrong place. A FITB question — "Set the speed to
 * [blank] today." — came out as "Set the speed to today." followed by a stray "Blank 1", so the
 * sentence read as though nothing were missing. `The <strong>fast</strong> car` had the same fault
 * and read "The car" then "fast".
 *
 * So the flow is walked in document order and inline things are substituted where they SIT: an
 * input contributes its accessible name, a formula its announcement, an image its alt. That is
 * what makes the read-through answer the question it exists for — does this sentence make sense
 * heard — rather than listing the pieces it happens to be built from.
 */
function inlineFlow(block: Element): { text: string; only?: 'formula' | 'reaction' } {
  const parts: string[] = [];
  /**
   * A block holding NOTHING but one formula is a formula, and says so in the list.
   *
   * Tracked here rather than guessed at afterwards, because the distinction is about what else the
   * block contains. A formula inside a sentence stays part of that sentence — it is being read as
   * prose and labelling it separately would be wrong — but one standing alone is its own kind of
   * thing, as an image is, and a reader meets it as one.
   */
  let formulas = 0;
  let sole: 'formula' | 'reaction' | undefined;
  const visit = (node: Node) => {
    for (const child of node.childNodes) {
      if (child.nodeType === 3) {
        const text = (child.textContent ?? '').replace(/\s+/g, ' ');
        if (text.trim()) parts.push(text.trim());
        continue;
      }
      if (child.nodeType !== 1) continue;
      const el = child as Element;
      if (isHidden(el)) continue;
      // Atomic, and never recursed into: its text is the LaTeX annotation and mhchem's phantom X.
      if (el.matches?.(FORMULA_SELECTOR)) {
        const spoken = formulaAnnouncement(el);
        if (spoken.name) parts.push(spoken.name);
        formulas += 1;
        sole = el.hasAttribute('data-reaction-formula') ? 'reaction' : 'formula';
        continue;
      }
      // NOT inlined, and skipped rather than recursed: an image keeps its own row. Rich text here
      // has no image extension, so an image is always its own block — inlining the alt as well
      // simply reported it twice, once in the flow and once as the image. Its own row is also what
      // carries the missing-alt problem, and what puts the silence in the right place.
      if (el.tagName === 'IMG') continue;
      // The image's own motion description, already announced as its description on the row above.
      // Walking into it would read it a second time as prose.
      if (el.hasAttribute?.('data-motion-description')) continue;
      // An inline answer slot — a FITB blank, a dropdown in a sentence — announces its name right
      // here, which is where a listener meets it.
      if (isControl(el)) {
        const name = accessibleName(el);
        if (name) parts.push(name);
        continue;
      }
      /*
       * A LABELLED NON-CONTROL contributes its label, which is what "name from content" does.
       *
       * Anything carrying `aria-label` names ITSELF and its text, if any, is not what a listener
       * hears — `<div role='img' aria-label='…'>` being the standard case, and the one that made
       * this a blind spot: a sortable item whose media had failed held nothing but that div, so
       * the SCAN called the button unnamed while every browser named it from the label. Over-
       * reporting an unnamed control is the failure mode the whole file is written to avoid, and
       * it was over-reporting against the teacher-facing report too.
       */
      const own = el.getAttribute?.('aria-label')?.trim();
      if (own) { parts.push(own); continue; }
      visit(el);
    }
  };
  visit(block);
  return {
    // Joined with spaces, then the space before punctuation taken back out — an inline thing at
    // the end of a clause would otherwise read as "Blank 2 ." on screen and pause oddly aloud.
    text: parts.join(' ').replace(/\s+/g, ' ').replace(/\s+([.,;:!?])/g, '$1').trim(),
    // One formula and nothing else. Two formulas side by side are a sentence made of formulas.
    ...(formulas === 1 && parts.length === 1 && sole ? { only: sole } : {}),
  };
}

/**
 * What a listener receives, in order.
 *
 * Every row is marked `authored` or not, and the two consumers want different halves. PROBLEMS
 * spans both, because a missing alt is a fault wherever it sits. The READ-THROUGH shows only
 * authored rows, and that boundary was a correction: reading order is not a property a teacher can
 * break here — a task is a vertical stack of blocks and each block's internal order is ours — so
 * listing our controls and containers gave them a long inventory with nothing actionable in it.
 * What IS theirs is whether their own words, images and formulas make sense heard.
 *
 * Not a screen-reader emulation, and it must never be presented as one: JavaScript cannot read the
 * platform accessibility tree, so this is Agavi's reconstruction. Roles and states are deliberately
 * absent for the same reason the controls are — they are ours, not the teacher's.
 */
/**
 * One image's row.
 *
 * ALWAYS authored, unlike everything else here, and by ancestry it would not be. The image block
 * renders its `<img>` in a plain container; only the caption beside it is rich text. So asking
 * `isAuthoredContent` dropped every image out of the read-through and the alt text went missing
 * from the reading, which is the opposite of the point: alt text is the single most commonly missed
 * authored thing, and hearing it in place is most of why a read-through is worth having.
 *
 * EMPTY alt counts as missing, which is deliberately NOT the general HTML rule. In general `alt=""`
 * is a considered "decorative, skip me" and flagging it trains authors to add junk alt text. But
 * this app has no way to express that: the image block renders `alt={effectiveAlt}` from a single
 * optional field, with no decorative option anywhere, and its OWN issues panel already reports a
 * missing one as an error. So an empty alt here means "not written yet", and treating it as
 * intentional would make this report contradict the editor about the same image. Revisit if a
 * decorative toggle is ever added.
 */
function imageRow(el: Element): OutlineRow {
  const alt = (el.getAttribute('alt') ?? '').trim();
  /*
   * An ANIMATED image owes a second thing, and for a different reason.
   *
   * Alt text describes what the image IS, which for a still is a near-lossless substitute. An
   * animation's information lives in the difference between frames, so alt text of the poster
   * gives the setup and leaves out the phenomenon. WCAG 1.2.1 — it is prerecorded video-only
   * content, and the file extension is all that disguises it.
   *
   * Read from `aria-describedby` rather than from the block's data, because this report walks the
   * rendered DOM: what it says is what a screen reader would actually receive, so a description
   * that is written but not wired up has to show up here as missing.
   *
   * BOTH ARE LISTED when both are absent, as two entries.
   *
   * A missing alt used to win outright, on the reasoning that it is the one to fix first. It is —
   * but reporting it alone hid the second obligation until the first was met: write the alt text,
   * watch the row clear, and a new row appears asking for something nobody mentioned. Alt text
   * first, since it is what names the image at all, then the description.
   */
  const animated = el.hasAttribute('data-animated');
  return {
    kind: 'image',
    name: alt,
    ...(animated ? { animated: true as const } : {}),
    authored: true,
    el,
  };
}

/** One control's row. Authored when the teacher's own words are what it says — see
 * `hasAuthoredName`. The scopes then differ in HOW it is read, not whether: Your Content speaks the
 * words alone, With Interactives adds the role and state around them. */
function controlRow(el: Element): OutlineRow {
  const states = controlStates(el);
  const value = controlValue(el);
  /*
   * NO DESCRIPTION ON A CONTROL, deliberately, and this was briefly wrong.
   *
   * Surfacing `aria-describedby` here printed text the panel never spoke — `useReadAloud` falls
   * back to the row's name — and on DataViz it printed it twice over, because the sonification
   * scrubber's description points at the chart's orientation clauses, which are already their own
   * `sr-only` paragraphs earlier in the outline.
   *
   * The case it was reaching for, an MCQ media option's motion description, is solved where it
   * belongs instead: that text is now part of the option's NAME. See `mediaAccessibleName`.
   */
  const animatedInside = !!el.querySelector?.('img[data-animated]');
  /* `data-media-transcript` is what the Sound player marks itself with — see the `media` row
     below, which reads the same attribute to ask whether a transcript was written. */
  const mediaKind = el.querySelector?.('img') ? 'image' as const
    : el.querySelector?.('[data-media-transcript]') ? 'audio' as const
    : undefined;
  return {
    kind: 'control',
    name: accessibleName(el),
    authored: hasAuthoredName(el),
    role: controlRole(el),
    ...(animatedInside ? { animated: true as const } : {}),
    ...(mediaKind ? { mediaKind } : {}),
    ...(states.length ? { states } : {}),
    ...(value ? { value } : {}),
    /*
     * NO PROBLEM REPORTED HERE, though an animated option does owe a motion description.
     *
     * Briefly this asked for one, by looking for a description on the control. It cannot any more
     * and should not: the text is part of the option's NAME now, which this cannot take apart, and
     * the block's own card already flags `media-motion-description-missing` as an error against
     * the field that fixes it. Asking twice, in two places, from two different readings of the
     * same data, is how the two come to disagree.
     */
    el,
  };
}

/**
 * A data table, as ONE row — the only place this deliberately does not mirror a reader line for
 * line, and the reason is that a reader does not read a table the way it reads prose.
 *
 * DataViz's accessible table is 177 rows of two cells. Walked as prose that became 354 entries,
 * each an isolated number with its column header thrown away — "1850", "-0.523", "1851" — which is
 * both far more than a reader says and less than it says, since `<th scope='col'>` is exactly what
 * a reader uses to announce "Year 1850, Deviation minus 0.523".
 *
 * What a reader really does is announce the table and its size on entering, read rows WITH their
 * headers, and let the user skip the whole thing with one keystroke. We have no skip, so a faithful
 * per-row listing would be a flood a teacher cannot get past — and nobody is going to audit whether
 * data point 94 reads correctly. Decided deliberately: the table is announced and its contents are
 * not enumerated. The listener's own reader will read them; the teacher needs to know it is there,
 * named, and the right size.
 *
 * `null` for a table holding CONTROLS — an editable grid is a widget, not a data table, and
 * swallowing it would hide controls that do need checking.
 */
function tableRow(el: Element): OutlineRow | null {
  if (el.querySelector('button, input, select, textarea, a[href], [role]')) return null;
  const bodyRows = el.querySelectorAll('tbody tr');
  const rows = bodyRows.length || el.querySelectorAll('tr').length;
  const columns = Math.max(0, ...[...el.querySelectorAll('tr')].map((r) => r.children.length));
  const caption = el.querySelector('caption')?.textContent?.trim() ?? '';
  return { kind: 'table', name: caption, authored: false, table: { rows, columns }, el };
}

/**
 * OUR OWN block, read in the order a listener meets it.
 *
 * Authored prose is one flow, because a FITB blank belongs inside its sentence. Our chrome is not a
 * sentence — a Capture button sits BESIDE a coordinate, not inside a clause — so treating it as one
 * would both swallow the control's role and put it in the wrong place: `inOrder` reaches a wrapper
 * before its children, so a single row for the wrapper announced the text ahead of a button that
 * visually precedes it. Reading order is the thing this report is about, so that is not a detail to
 * leave approximate.
 *
 * The runs of text between the controls are what a reader speaks as lines, and the controls and
 * images keep their own rows where they sit. Everything yielded here is consumed, so the main walk
 * must not visit it again.
 */
function* chromeSequence(block: Element): Generator<OutlineRow> {
  let run: string[] = [];
  const flush = function* (): Generator<OutlineRow> {
    const text = run.join(' ').replace(/\s+/g, ' ').replace(/\s+([.,;:!?])/g, '$1').trim();
    run = [];
    if (text) yield { kind: 'text', name: text, authored: false, el: block };
  };
  const visit = function* (node: Node): Generator<OutlineRow> {
    for (const child of node.childNodes) {
      if (child.nodeType === 3) {
        const text = (child.textContent ?? '').replace(/\s+/g, ' ');
        if (text.trim()) run.push(text.trim());
        continue;
      }
      if (child.nodeType !== 1) continue;
      const el = child as Element;
      if (isHidden(el)) continue;
      if (el.tagName === 'IMG') { yield* flush(); yield imageRow(el); continue; }
      if (isControl(el)) { yield* flush(); yield controlRow(el); continue; }
      /*
       * The image's own motion description, already carried as that image's DESCRIPTION.
       *
       * The authored-prose walker skips this and this one did not, so the text was announced
       * twice: once on the image row and again, a line later, as a bare run of chrome text with
       * nothing to say whose it was. The Image block takes THIS path — its `<img>` sits in a plain
       * container, not in rich text — so the skip that existed was never the one being used.
       */
      if (el.hasAttribute?.('data-motion-description')) continue;
      yield* visit(el);
    }
  };
  yield* visit(block);
  yield* flush();
}

export function screenReaderOutline(root: Element): OutlineRow[] {
  const rows: OutlineRow[] = [];
  const formulas: Element[] = [];
  /**
   * A control is ATOMIC, and this is what makes it so.
   *
   * Reported: an option read as "checkbox, not checked, 10 m/s" and then "10 m/s" again. A screen
   * reader announces a control ONCE — its name already IS its content, whether that came from an
   * `aria-label` or from the text inside — so emitting rows for what it wraps says everything
   * twice. Worse where the content is a formula: the option announced the markup and then the
   * formula announced itself properly, so the code came first.
   */
  const controls: Element[] = [];
  /** Announced whole, contents not enumerated — see `tableRow`. */
  const tables: Element[] = [];
  for (const el of inOrder(root)) {
    // Inside a formula, a table or a control already reported: skip. All were emitted when reached.
    if (formulas.some(f => f.contains(el))) continue;
    if (tables.some(tb => tb.contains(el))) continue;
    if (controls.some(c => c.contains(el))) continue;
    if (el.tagName === 'TABLE') {
      const row = tableRow(el);
      if (row) { tables.push(el); rows.push(row); continue; }
    }
    // Formulas are consumed by the flow of the block they sit in, so they get no row of their own
    // — that is what puts them in the right place in the sentence.
    if (el.matches?.(FORMULA_SELECTOR)) { formulas.push(el); continue; }
    const tag = el.tagName;
    if (tag === 'IMG') {
      // EMPTY alt counts as missing here, which is deliberately NOT the general HTML rule.
      //
      // In general, `alt=""` is a considered "decorative, skip me" and flagging it trains authors
      // to add junk alt text. But this app has no way to express that: the image block renders
      // `alt={effectiveAlt}` from a single optional field, with no decorative option anywhere, and
      // its OWN issues panel already reports a missing one as an error. So an empty alt here means
      // "not written yet", and treating it as intentional would make this report contradict the
      // editor about the same image. Revisit if a decorative toggle is ever added.
      /**
       * ALWAYS authored, unlike everything else here — and by ancestry it would not be.
       *
       * The image block renders its `<img>` in a plain container; only the caption beside it is
       * rich text. So asking `isAuthoredContent` dropped every image out of the read-through and
       * the alt text went missing from the reading, which is the opposite of the point: alt text is
       * the single most commonly missed authored thing, and hearing it in place is most of why a
       * read-through is worth having.
       *
       * True regardless of position is right rather than convenient: an `alt` comes from a
       * teacher's own field, and Agavi's own imagery is SVG icons, not `<img>`. Problems never
       * consulted this flag for images anyway, so nothing new can be flagged by it.
       */
      rows.push(imageRow(el));
      continue;
    }
    // Media whose transcript is missing. Reported here as well as in the block's own issues panel
    // because this report is where a teacher looks for everything that is THEIRS to fix, and a
    // clip with no transcript is exactly that.
    if (el.hasAttribute('data-media-transcript')) {
      // Authored for the same reason an image is: a transcript is a teacher's field, and the sound
      // block wraps its player in plain chrome.
      rows.push({ kind: 'media', name: '', authored: true, el });
      continue;
    }
    if (isControl(el)) {
      // Never flagged, and never `authored`, so the read-through leaves it out entirely. A control
      // with no name is OUR bug — every control here is rendered by a block and no authoring
      // surface can produce a nameless one — so it belongs in our own test suite, not in a teacher's
      // panel. `blocks-have-accessible-names.test.tsx` is where that check lives now. Rows are still
      // emitted so anything auditing the whole tree can find them.
      // Never authored, because a control never needs a row of its OWN in the read-through: an
      // inline one — a FITB blank, a dropdown in a sentence — is spoken inside its block's flow,
      // where a listener meets it, and one of ours outside all authored content is ours. Rows are
      // still emitted so anything auditing the whole tree can see them.
      controls.push(el);
      rows.push(controlRow(el));
      continue;
    }
    if (/^H[1-6]$/.test(tag)) {
      rows.push({ kind: 'heading', name: accessibleName(el), authored: isAuthoredContent(el, root), el });
      continue;
    }
    // The innermost BLOCK, read as one flow. Innermost so a paragraph is not repeated by every
    // wrapper above it, and a whole block rather than its own text nodes so that whatever sits
    // between them — a blank, a formula, a bold word — lands where a listener meets it.
    if (!el.matches?.(BLOCK_TAGS) || el.querySelector?.(BLOCK_TAGS)) continue;
    /**
     * OUR static text gets a row too, and used not to.
     *
     * This gate used to be `if (!isAuthoredContent(el, root)) continue`, which dropped every word we
     * render before a row existed — so no scope could show it, including the one that promises the
     * whole experience. What that lost was not decoration: GPS's "Within 10 m of the target" is the
     * precision the task is graded on, and its live readout is the answer the student is giving. A
     * listener hears both; the emulator reported neither, and the markup was never at fault.
     *
     * A reader does not only tab, it reads — so the full scope is incomplete without the prose
     * between the controls, and `authored: false` keeps Your Content exactly as it was.
     */
    if (!isAuthoredContent(el, root)) {
      for (const row of chromeSequence(el)) {
        // Whatever the sequence emitted a row for is spoken; the walk must not reach it again.
        if (row.kind === 'control' || row.kind === 'image') controls.push(row.el);
        rows.push(row);
      }
      continue;
    }
    const flow = inlineFlow(el);
    if (flow.text) {
      /**
       * Authored text sitting inside a graded feedback box is FEEDBACK, not prose.
       *
       * A feedback box announces itself to the eye — tinted fill, a big tick or cross, a coloured
       * rail on the block — and none of that reaches a listener: the glyph is `aria-hidden`, the
       * rest is CSS. So the emulator reported the teacher's sentence bare, exactly as a screen
       * reader would have, with nothing to separate it from the question above it.
       *
       * `data-eval` is the marker, set by `FiredFeedback` for the same reason Preview's container
       * reads it: the result has to be legible from outside the block. One attribute, three readers.
       *
       * The row keeps the teacher's own words as its name, and the verdict rides alongside as a
       * kind — the structure images, formulae and reactions already use, so a listener meets
       * "Feedback, check mark symbol, that is the correct answer" and then the sentence.
       */
      const evaluation = el.closest?.('[data-eval]')?.getAttribute('data-eval');
      rows.push(evaluation === 'correct' || evaluation === 'incorrect' || evaluation === 'neutral'
        ? { kind: 'feedback', name: flow.text, authored: true, evaluation, el }
        : { kind: flow.only ?? 'text', name: flow.text, authored: true, el });
    }
  }
  /**
   * Mark the controls an authored sentence already spoke.
   *
   * A post-pass rather than a check inside the walk, because the two rows are produced in the wrong
   * order to compare as they are made: a block is visited BEFORE the control inside it, so at the
   * moment the control is reached its sentence exists, but at the moment the sentence is built the
   * control has no row yet. Deciding afterwards, when both exist, is simpler than threading state
   * through the walk — and it is the containment that matters, not the order.
   */
  const spoken = rows.filter(r => r.authored && (r.kind === 'text' || r.kind === 'heading')).map(r => r.el);
  for (const row of rows) {
    if (row.kind === 'control' && spoken.some(s => s !== row.el && s.contains(row.el))) row.inline = true;
  }
  return rows;
}

// ── Report 2 · contrast ───────────────────────────────────────────────────────

/**
 * The floor, and why it is not AA.
 *
 * The palette deliberately does not guarantee AA — see the note in `ink-colors.ts`. Reporting
 * against 4.5:1 would audit a decision that was already made on purpose.
 *
 * 3:1 is WCAG's own bar for large text and for non-text contrast — roughly where something stops
 * being PERCEIVABLE rather than merely sub-optimal. Note what this does NOT relax: large text
 * already required exactly 3:1 under AA, so the size allowance it used to carry is now the rule for
 * everything, and no large-text case got looser.
 *
 * The palette now clears this floor itself on every tone but the deliberately subtle light ones, so
 * in practice the floor only ever judges colours that did not come from us.
 */
export const CONTRAST_FLOOR = 3;

/**
 * The two card surfaces authored text sits on, one per theme.
 *
 * Needed because the report has to answer for the theme you are NOT looking at, and the tones swap
 * between them: mid amber is 2.1:1 on white and 8.1:1 on navy. Reading computed styles can only ever
 * see the current theme, so a teacher reviewing in dark mode was told nothing about the light-mode
 * reader — a finding that silently depends on the reviewer's own theme is worse than no finding.
 *
 * `ink-colors.test.ts` asserts these still match `--ws-surface` in globals.css.
 */
export const PREVIEW_SURFACES = { light: '#FFFFFF', dark: '#071A35' } as const;

/** WCAG relative luminance. */
function luminance([r, g, b]: [number, number, number]): number {
  const f = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

export function contrastRatio(fg: [number, number, number], bg: [number, number, number]): number {
  const [a, b] = [luminance(fg), luminance(bg)];
  const [hi, lo] = a >= b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

